// PurchaseOrderPreviewItems.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Utilities
import { formatPrice } from 'utilities/Currency';

// Local
import { PURCHASE_ORDER_ITEM_TYPE_KEY } from 'nodetypes/purchaseorderitem/purchaseOrderItemKeys';
import {
  computePurchaseOrderItemsRollups,
  getPurchaseOrderItemRowModel,
  readPurchaseOrderItemField,
} from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';

// CSS
import './PurchaseOrderItems.css';
import './PurchaseOrderPreviewItems.css';

function cleanString(value) {
  return String(value || '').trim();
}

function formatMoneyCell(value) {
  if (value === null || value === undefined || value === '') {
    return <span className="purchase-order-items-muted">—</span>;
  }
  return formatPrice(value);
}

function formatPlainCell(value) {
  const cleaned = cleanString(value);
  if (!cleaned) return <span className="purchase-order-items-muted">—</span>;
  return cleaned;
}

const ItemThumb = React.memo(({ itemKey, coverUrl }) => {
  const [src, setSrc] = useState(coverUrl || '');

  useEffect(() => {
    let cancelled = false;
    const raw = cleanString(coverUrl);
    if (!raw) {
      setSrc('');
      return undefined;
    }
    if (
      raw.startsWith('blob:')
      || raw.startsWith('data:')
      || raw.startsWith('http://')
      || raw.startsWith('https://')
      || raw.startsWith('/')
    ) {
      setSrc(raw);
      return undefined;
    }
    (async () => {
      try {
        const signed = await resolveImageUrl({ nodeKey: itemKey || null, url: raw });
        if (!cancelled) setSrc(signed || raw);
      } catch {
        if (!cancelled) setSrc(raw);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coverUrl, itemKey]);

  if (!src) return null;

  return (
    <div className="purchase-order-items-thumb" aria-hidden="true">
      <img src={src} alt="" draggable="false" loading="lazy" />
    </div>
  );
});

ItemThumb.displayName = 'ItemThumb';

/**
 * PurchaseOrderPreviewItems
 *
 * Read-only vendor-facing items table for Purchase Order Preview.
 *
 * @param {object} node - Purchase order node.
 * @param {object|null} [nodeType]
 * @param {object[]} [neighborNodes]
 * @returns {ReactNode}
 */
const PurchaseOrderPreviewItems = React.memo(({
  node,
  nodeType = null,
  neighborNodes = [],
}) => {
  // ----- CONTEXT ----- //

  const { getType } = useNodeTypes();
  const language = useCurrentLanguage();

  // ----- MEMOS ----- //

  const itemType = useMemo(() => getType(PURCHASE_ORDER_ITEM_TYPE_KEY), [getType]);

  const items = useMemo(() => (
    (Array.isArray(neighborNodes) ? neighborNodes : [])
      .filter((row) => cleanString(row?.type).toUpperCase() === PURCHASE_ORDER_ITEM_TYPE_KEY)
      .sort((left, right) => {
        const leftOrder = Number(readPurchaseOrderItemField(left, 'sortOrder'));
        const rightOrder = Number(readPurchaseOrderItemField(right, 'sortOrder'));
        if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return cleanString(left?.title).localeCompare(cleanString(right?.title));
      })
  ), [neighborNodes]);

  const rows = useMemo(
    () => items.map((item) => ({
      item,
      model: getPurchaseOrderItemRowModel(item, itemType, language),
    })),
    [itemType, items, language],
  );

  const rollups = useMemo(
    () => computePurchaseOrderItemsRollups(items, node, nodeType, null),
    [items, node, nodeType],
  );

  if (!node) return null;

  // ----- RENDER ----- //

  return (
    <div className="purchase-order-preview-items-container">
      <div className="purchase-order-items-title">
        {`Items (${rows.length})`}
      </div>

      {rows.length === 0 ? (
        <div className="purchase-order-items-empty">
          No items yet.
        </div>
      ) : (
        <div className="purchase-order-items-scroll">
          <table className="purchase-order-items-table purchase-order-preview-items-table">
            <thead>
              <tr>
                <th className="purchase-order-items-col-line" scope="col">#</th>
                <th className="purchase-order-items-col-item">Item</th>
                <th className="purchase-order-items-col-sku">SKU</th>
                <th className="purchase-order-items-col-status">Status</th>
                <th className="purchase-order-items-col-manufacturer">Manufacturer</th>
                <th className="purchase-order-items-col-qty">Qty</th>
                <th className="purchase-order-items-col-unit">Unit</th>
                <th className="purchase-order-items-col-unit-price">Unit Price</th>
                <th className="purchase-order-items-col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ model }, index) => (
                <tr key={model.key || index}>
                  <td className="purchase-order-items-col-line">{index + 1}</td>
                  <td className="purchase-order-items-col-item">
                    <div className="purchase-order-items-item-cell">
                      <ItemThumb itemKey={model.key} coverUrl={model.coverUrl} />
                      <div className="purchase-order-items-item-text">
                        <div className="purchase-order-preview-items-title">
                          {model.title || 'Untitled'}
                        </div>
                        {model.description ? (
                          <div className="purchase-order-preview-items-description">
                            {model.description}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td>{formatPlainCell(model.sku)}</td>
                  <td>{formatPlainCell(model.status)}</td>
                  <td>{formatPlainCell(model.manufacturer)}</td>
                  <td className="purchase-order-items-col-qty">
                    {model.quantity == null
                      ? <span className="purchase-order-items-muted">—</span>
                      : model.quantity}
                  </td>
                  <td className="purchase-order-items-col-unit">
                    {formatPlainCell(model.unitLabel || model.unit)}
                  </td>
                  <td className="purchase-order-items-col-unit-price">
                    {formatMoneyCell(model.unitPrice)}
                  </td>
                  <td className="purchase-order-items-col-total">
                    {formatMoneyCell(model.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="purchase-order-items-footer">
        <div className="purchase-order-items-footer-row">
          <span className="purchase-order-items-footer-label">Subtotal</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.subTotal)}
          </span>
        </div>
        <div className="purchase-order-items-footer-row">
          <span className="purchase-order-items-footer-label">Tax</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.tax)}
          </span>
        </div>
        <hr className="purchase-order-items-footer-rule" />
        <div className="purchase-order-items-footer-row">
          <span className="purchase-order-items-footer-label">Total</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.total)}
          </span>
        </div>
        <div className="purchase-order-items-footer-row">
          <span className="purchase-order-items-footer-label">Amount Paid</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.amountPaid)}
          </span>
        </div>
        <hr className="purchase-order-items-footer-rule" />
        <div className="purchase-order-items-footer-row is-emphasis">
          <span className="purchase-order-items-footer-label">Balance Due</span>
          <span className="purchase-order-items-footer-value">
            {formatPrice(rollups.balanceDue)}
          </span>
        </div>
      </div>
    </div>
  );
});

// Dev Tools
PurchaseOrderPreviewItems.displayName = 'PurchaseOrderPreviewItems';

export default PurchaseOrderPreviewItems;
